import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Brain, MessageSquare, Users, TrendingUp, Sparkles, Send, 
  ChevronRight, Target, AlertTriangle, CheckCircle2, Plus,
  Zap, BarChart3, ArrowRight, User, RefreshCw, Lightbulb,
  UserPlus, UserMinus, ArrowUpRight, Shuffle, Shield, Heart,
  Activity, Star, Clock, Play, X, ChevronDown, Layers, HeartHandshake
} from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';
import { COGNITIVE_LANES, COGNITIVE_LEVELS, teamMembers } from '../mock';

// ================================================
// COLORS
// ================================================
const AI_COLORS = {
  primary: '#00C9A7',
  primaryLight: '#D1FAE5',
  primaryDark: '#059669',
  purple: '#8B5CF6',
  purpleLight: '#EDE9FE',
  blue: '#3B82F6',
  blueLight: '#DBEAFE',
  amber: '#F59E0B',
  amberLight: '#FEF3C7',
  chemistry: '#C2185B', // Warm rose-purple (was hot pink)
  chemistryLight: '#FCE4EC',
  chemistryDark: '#880E4F',
  surface: '#FFFFFF',
  background: '#F8FAFC',
  text: '#1E293B',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  headerDark: '#0F172A',
  soulFood: '#F59E0B' // Soul Food Division amber
};

// ================================================
// COACHING SCENARIOS & MOCK RESPONSES
// ================================================
const COACHING_SCENARIOS = [
  {
    id: 'wrong_lane',
    title: 'Lane Alignment Check',
    description: 'Feeling like your work doesn\'t match your strengths?',
    icon: Target,
    color: AI_COLORS.purple,
    initialPrompt: "I've been feeling like my work lately doesn't really play to my strengths. Can you help me understand my cognitive profile?",
    conversation: [
      {
        role: 'user',
        content: "I've been feeling like my work lately doesn't really play to my strengths. Can you help me understand my cognitive profile?"
      },
      {
        role: 'ai',
        content: "I've analyzed your cognitive profile and task history. Here's what I see:\n\n**Your Primary Lane: Vision & Expansion (L4)**\nYou're naturally wired for big-picture thinking, strategic planning, and seeing opportunities others miss.\n\n**But lately...**\nOver the past 6 weeks, 68% of your tasks have been in the **Execution lane** — detailed implementation work, process management, and tactical delivery.\n\n**The Impact:**\n- Your lane alignment score dropped from 85% to 62%\n- Your energy levels (based on completion patterns) show afternoon fatigue\n- You're spending cognitive energy on work that doesn't energize you\n\n**My Recommendation:**\nYou're a Vision thinker doing Execution work. That's like asking a chef to do accounting — they *can*, but it drains them.\n\nWould you like me to:\n1. Identify which Execution tasks could be delegated?\n2. Show you Vision-aligned opportunities in current projects?\n3. Draft a conversation guide for discussing this with your manager?"
      },
      {
        role: 'user',
        content: "Yes, show me which tasks could be delegated and who might be better suited for them."
      },
      {
        role: 'ai',
        content: "**Tasks to Consider Delegating:**\n\n1. **Weekly Status Report Compilation** (3 hrs/week)\n   → Best fit: **David Park** (Operations L3, currently at 45% capacity)\n   → Match Score: 94%\n\n2. **Vendor Contract Reviews** (2 hrs/week)\n   → Best fit: **James Liu** (Finance L4, has contract expertise)\n   → Match Score: 91%\n\n3. **Sprint Planning Documentation** (2 hrs/week)\n   → Best fit: **Marcus Rivera** (Operations L4, already leads similar work)\n   → Match Score: 89%\n\n**If you delegate these 7 hours/week:**\n- Your Vision lane time increases from 32% → 58%\n- Projected lane alignment: 62% → 81%\n- Estimated burnout risk reduction: 15 points\n\n**Ready to take action?**\nI can draft delegation requests for your manager's approval, or we can explore what Vision-aligned work you'd fill that time with instead."
      }
    ]
  },
  {
    id: 'overwhelmed',
    title: 'Cognitive Overload',
    description: 'Feeling stretched too thin? Let\'s find balance.',
    icon: Activity,
    color: AI_COLORS.amber,
    initialPrompt: "I'm completely overwhelmed. I have too many things going on and I can't seem to catch up.",
    conversation: [
      {
        role: 'user',
        content: "I'm completely overwhelmed. I have too many things going on and I can't seem to catch up."
      },
      {
        role: 'ai',
        content: "I hear you, and your data confirms what you're feeling. Let me show you what's happening:\n\n**Current State:**\n- Active tasks: **14** (team average: 6)\n- Capacity utilization: **127%** (healthy range: 60-80%)\n- Cross-lane work: You're operating in **4 different lanes** this week\n- Overdue items: 3 (unusual for your track record)\n\n**The Hidden Problem:**\nIt's not just volume — it's cognitive switching. You're jumping between Strategy, Operations, Marketing, and Execution work. Each switch costs mental energy.\n\n**Immediate Relief Options:**\n\n1. **Emergency Triage** (I can do this now)\n   - Flag 5 tasks as delegate-able based on cognitive match\n   - Identify 3 tasks that can be deferred without impact\n\n2. **Load Rebalancing** \n   - Your teammate Lisa Chen is at 38% capacity in your same lane\n   - Marcus has bandwidth for Operations tasks\n\n3. **Protective Boundaries**\n   - Block 2 hours daily for deep work\n   - Batch similar-lane tasks together\n\nWhich would help most right now?"
      },
      {
        role: 'user',
        content: "Let's do the emergency triage. Show me what can be delegated or deferred."
      },
      {
        role: 'ai',
        content: "**EMERGENCY TRIAGE COMPLETE**\n\n🔴 **Delegate Immediately (5 tasks, 12 hours):**\n\n| Task | Best Match | Their Capacity |\n|------|-----------|----------------|\n| Budget variance report | James Liu | 52% |\n| Team meeting facilitation | David Park | 45% |\n| Vendor outreach emails | Lisa Chen | 38% |\n| Process documentation | Marcus Rivera | 61% |\n| Client deck formatting | Emma Thompson | 55% |\n\n🟡 **Defer Without Impact (3 tasks):**\n- Q3 planning deck → due in 3 weeks, no dependencies\n- Training material updates → can shift 2 weeks\n- Internal wiki cleanup → low urgency\n\n🟢 **Keep & Prioritize (6 tasks):**\nThese are in your primary lane and high-impact.\n\n**Projected Impact:**\n- Capacity drops: 127% → 72% ✓\n- Single-lane focus time: +6 hours/week\n- Estimated stress reduction: Significant\n\n**Ready to Execute?**\nI can generate delegation requests with context for each person, or flag this to your manager with my analysis."
      }
    ]
  },
  {
    id: 'promotion',
    title: 'Career Growth',
    description: 'Am I ready for the next level?',
    icon: TrendingUp,
    color: AI_COLORS.primary,
    initialPrompt: "I've been wondering if I'm ready for a promotion. How do I know when it's time?",
    conversation: [
      {
        role: 'user',
        content: "I've been wondering if I'm ready for a promotion. How do I know when it's time?"
      },
      {
        role: 'ai',
        content: "Great question. Let me analyze your readiness objectively using your cognitive and performance data.\n\n**Current Level: L3 (Improve)**\n**Next Level: L4 (Decide + Lead)**\n\n**L4 Readiness Assessment:**\n\n✅ **Signals You're Ready:**\n- Operating at L4 behaviors for 8+ weeks\n- On-time completion: 94% (L4 benchmark: 85%+)\n- Cross-lane influence: Leading initiatives in 3 lanes\n- Decision quality: 91% of your escalations were validated\n- Mentoring: 2 L2 team members cite you as key supporter\n\n⚠️ **Growth Edges:**\n- Strategic communication: Your visibility to executives is limited\n- Delegation: You complete 23% of tasks that could be delegated\n- Stakeholder management: Limited cross-functional exposure\n\n**My Assessment: 78% Ready**\nYou're demonstrating L4 capabilities but haven't had visible opportunities to prove executive-level judgment.\n\n**Recommended Next Steps:**\n1. Request a stretch assignment with executive visibility\n2. Delegate more tactical work to build leadership presence\n3. Document 3 strategic decisions you've driven\n\nWant me to draft a career conversation guide for your manager?"
      }
    ]
  },
  {
    id: 'new_hire',
    title: 'New Hire Onboarding',
    description: 'Understanding your cognitive profile as a new team member',
    icon: UserPlus,
    color: AI_COLORS.blue,
    initialPrompt: "I just joined the team. Can you help me understand how I work best and how I fit into the organization?",
    conversation: [
      {
        role: 'user', 
        content: "I just joined the team. Can you help me understand how I work best and how I fit into the organization?"
      },
      {
        role: 'ai',
        content: "Welcome! I've analyzed your onboarding assessment and early work patterns. Here's your cognitive profile:\n\n**Your Primary Lane: Creative (L3)**\nYou think in possibilities, aesthetics, and user experience. You see solutions others miss and naturally gravitate toward innovation.\n\n**Secondary Lane: Marketing (L2)**\nYou understand how to communicate value and connect with audiences.\n\n**Your Superpowers:**\n- Pattern recognition in unstructured problems\n- Translating complex ideas into visual/experiential concepts\n- Energized by ambiguity and whitespace\n\n**Your Kryptonite:**\n- Detailed process documentation (drains you)\n- Repetitive operational tasks\n- Rigid timelines without creative latitude\n\n**How You Fit:**\nThe team currently has 2 Creative lane thinkers. You bring fresh perspective and will pair well with Emma Thompson (Creative L4) for mentorship.\n\n**First 30 Days Recommendation:**\n1. Shadow Emma on the Brand Campaign project\n2. Take ownership of one creative deliverable\n3. Avoid volunteering for Operations tasks (they'll drain you)\n\nWant me to suggest specific projects that match your profile?"
      }
    ]
  },
  {
    id: 'team_friction',
    title: 'Team Dynamics',
    description: 'Navigating difficult collaboration patterns',
    icon: Users,
    color: AI_COLORS.purple,
    initialPrompt: "I'm having trouble working with someone on my team. Our collaboration just isn't clicking.",
    conversation: [
      {
        role: 'user',
        content: "I'm having trouble working with someone on my team. Our collaboration just isn't clicking."
      },
      {
        role: 'ai',
        content: "I've noticed some friction patterns in your collaboration data. Let me share what I see:\n\n**Your Profile: Strategy Lane (L3)**\n- You think in frameworks, plans, and structured approaches\n- You prefer clarity before action\n- Decision style: Analytical, needs data\n\n**Likely Friction Partner: Execution Lane (L2-L3)**\n- They think in action, speed, and iteration\n- They prefer doing over planning\n- Decision style: Intuitive, trusts gut\n\n**The Cognitive Clash:**\nYou want to plan before acting. They want to act and adjust. Neither is wrong — it's a lane difference.\n\n**Common Friction Points:**\n- You feel they're \"rushing without thinking\"\n- They feel you're \"overthinking and slowing things down\"\n- Handoffs get messy because you speak different cognitive languages\n\n**Bridge Strategies:**\n\n1. **For You:** Share your \"why\" faster. They don't need the full framework — just the headline.\n\n2. **For Them:** Ask for a 2-minute buffer before they execute. Frame it as \"quick alignment.\"\n\n3. **Together:** Establish a \"Plan-Do-Check\" rhythm. You own Plan, they own Do, you both own Check.\n\nWant me to draft a conversation opener for addressing this directly?"
      }
    ]
  },
  {
    id: 'energy_management',
    title: 'Energy & Focus',
    description: 'Optimize your cognitive energy throughout the day',
    icon: Zap,
    color: AI_COLORS.amber,
    initialPrompt: "I feel productive in the morning but completely drained by afternoon. How can I manage my energy better?",
    conversation: [
      {
        role: 'user',
        content: "I feel productive in the morning but completely drained by afternoon. How can I manage my energy better?"
      },
      {
        role: 'ai',
        content: "Your completion patterns confirm this — 73% of your high-quality work happens before noon. Let's optimize around your natural rhythm.\n\n**Your Energy Profile:**\n- Peak hours: 8am - 12pm (high cognitive capacity)\n- Dip period: 1pm - 3pm (recovery needed)\n- Second wind: 3pm - 5pm (moderate capacity)\n\n**Current Problem:**\nYou're scheduling meetings during peak hours and saving deep work for the afternoon dip. That's backwards.\n\n**Optimized Schedule:**\n\n🌅 **8am - 12pm: Deep Work Block**\n- Your primary lane tasks (Strategy)\n- Complex decisions\n- Creative problem-solving\n- NO meetings if possible\n\n🌤️ **12pm - 2pm: Recovery Zone**\n- Lunch + light admin\n- Routine emails\n- Low-stakes meetings\n\n⚡ **2pm - 5pm: Collaboration Time**\n- Team syncs and 1:1s\n- Cross-functional meetings\n- Review and feedback tasks\n\n**Quick Wins:**\n1. Block 8-11am as \"Focus Time\" on your calendar\n2. Batch all meetings into afternoon slots\n3. Save email for the post-lunch dip\n\n**Projected Impact:**\n- Deep work time: +8 hours/week\n- Afternoon energy: Improved (you're not depleted from AM meetings)\n- Output quality: Higher (matching work to energy)\n\nWant me to analyze your calendar and suggest specific meeting moves?"
      }
    ]
  },
  {
    id: 'skill_growth',
    title: 'Skill Development',
    description: 'Expanding into new cognitive lanes',
    icon: Layers,
    color: AI_COLORS.blue,
    initialPrompt: "I want to develop skills in a new area. How should I approach expanding my cognitive range?",
    conversation: [
      {
        role: 'user',
        content: "I want to develop skills in a new area. How should I approach expanding my cognitive range?"
      },
      {
        role: 'ai',
        content: "Expanding your cognitive range is one of the highest-leverage career moves. Let me map your current profile and growth opportunities.\n\n**Current Profile:**\n- Primary: Operations (L4) — You're maxed here\n- Secondary: Execution (L3) — Strong foundation\n- Emerging: Strategy (L1) — Early signals\n\n**Natural Growth Path:**\nBased on your task patterns and feedback, **Strategy lane** is your logical next frontier. Here's why:\n\n- Operations → Strategy is a common L4 evolution\n- You're already asking \"why\" questions (Strategy signal)\n- 3 recent projects had you doing strategic framing\n\n**Development Plan:**\n\n**Phase 1: Shadow (Weeks 1-4)**\n- Join Sarah Chen's strategy sessions as observer\n- Ask to see how strategic decisions get made\n\n**Phase 2: Contribute (Weeks 5-8)**\n- Take one strategic subtask on a project\n- Get feedback on your strategic thinking\n\n**Phase 3: Own (Weeks 9-12)**\n- Lead a small strategic initiative\n- Present to stakeholders\n\n**Success Metrics:**\n- Strategy lane proficiency: L1 → L2\n- Cross-lane versatility increases your match potential by 34%\n- Opens path to Director-level roles\n\nWant me to identify specific projects where you could start Phase 1?"
      }
    ]
  },
  {
    id: 'burnout_recovery',
    title: 'Burnout Recovery',
    description: 'Rebuilding after a period of high stress',
    icon: Heart,
    color: '#EC4899',
    initialPrompt: "I think I've been burned out for a while. How do I recover while still doing my job?",
    conversation: [
      {
        role: 'user',
        content: "I think I've been burned out for a while. How do I recover while still doing my job?"
      },
      {
        role: 'ai',
        content: "Thank you for trusting me with this. Your data suggests you've been in an elevated state for 6+ weeks. Recovery is possible — and necessary. Let me help.\n\n**Your Burnout Profile:**\n- Risk Score: 72 (Elevated)\n- Duration: 6 weeks above healthy threshold\n- Primary Drivers: Sustained overload + lane misalignment\n- Recovery Capacity: Currently limited (you're depleted)\n\n**The Reality:**\nYou can't \"push through\" burnout. That's like running on a broken leg. But you also can't disappear. Here's the middle path:\n\n**Week 1-2: Stabilize**\n- Reduce to 70% capacity (I'll help identify what to pause)\n- No new commitments\n- 2 hours daily protected for non-work\n\n**Week 3-4: Recover**\n- Shift toward primary lane work (it energizes rather than drains)\n- One \"win\" per day — small completions rebuild confidence\n- Delegate 3 cognitive-draining tasks\n\n**Week 5-6: Rebuild**\n- Gradually increase to 85% capacity\n- Establish sustainable boundaries\n- Monitor weekly with me\n\n**What I Can Do Right Now:**\n1. Flag your status to your manager (confidentially) with recovery plan\n2. Identify 5 tasks to pause or delegate\n3. Block recovery time on your calendar\n\nThis isn't weakness — it's maintenance. How would you like to proceed?"
      }
    ]
  },
  {
    id: 'leadership_style',
    title: 'Leadership Style',
    description: 'Understanding how you lead based on your cognitive profile',
    icon: Star,
    color: AI_COLORS.primary,
    initialPrompt: "I'm moving into a leadership role. What kind of leader will I naturally be based on my profile?",
    conversation: [
      {
        role: 'user',
        content: "I'm moving into a leadership role. What kind of leader will I naturally be based on my profile?"
      },
      {
        role: 'ai',
        content: "Congratulations! Let me translate your cognitive profile into leadership style.\n\n**Your Profile: Vision Lane (L4)**\n\n**Your Natural Leadership Style: The Architect**\n\nYou lead by painting compelling futures and connecting people to purpose. You're not a micro-manager — you're a direction-setter.\n\n**Strengths as a Leader:**\n- Inspiring teams with clear vision\n- Seeing opportunities others miss\n- Making bold, directional decisions\n- Giving people autonomy within a framework\n\n**Watch-Outs:**\n- May skip over operational details (need an Ops partner)\n- Can frustrate detail-oriented team members\n- Vision without execution creates skepticism\n\n**Your Ideal Team Composition:**\n- Operations L3-L4 partner (translates your vision to plans)\n- Execution L2-L3s (make things happen)\n- Another Vision thinker (thought partner)\n\n**Leadership Practices That Fit You:**\n\n1. **Weekly Vision Sync** — Share the \"why\" behind the work\n2. **Delegate the \"how\"** — Trust your Ops partner\n3. **Quarterly Direction Setting** — Your superpower session\n4. **Skip-Level 1:1s** — Stay connected to the work without micromanaging\n\n**Your Leadership Development Edge:**\nLearn to slow down for people who need more detail. Not everyone sees what you see immediately.\n\nWant me to suggest how to structure your first team meeting?"
      }
    ]
  },
  {
    id: 'giving_feedback',
    title: 'Difficult Feedback',
    description: 'Preparing for a challenging performance conversation',
    icon: MessageSquare,
    color: '#EC4899',
    initialPrompt: "I need to give tough feedback to someone on my team about their performance. How should I approach this?",
    conversation: [
      {
        role: 'user',
        content: "I need to give tough feedback to someone on my team about their performance. How should I approach this?"
      },
      {
        role: 'ai',
        content: "Delivering difficult feedback is one of the most important — and hardest — leadership skills. Let me help you prepare.\n\n**First, let me understand the situation:**\n\nBased on your team data, I see a few people who might need attention. But before diving in, let's make sure your feedback approach matches their cognitive profile. Different people receive feedback differently.\n\n**The Framework I Recommend:**\n\n**1. Context + Specific Observation**\n\"In the last sprint, I noticed X happened three times...\"\nNot: \"You always do X\" (feels like attack)\n\n**2. Impact Statement**\n\"The impact was that the team had to...\" or \"It caused delays in...\"\nThis connects behavior to outcome objectively.\n\n**3. Curiosity First**\n\"Help me understand what was happening for you.\"\nThis opens dialogue vs. creating defensiveness.\n\n**4. Collaborative Problem-Solving**\n\"What would help you do X differently?\" or \"What support do you need?\"\n\n**For Different Cognitive Profiles:**\n\n- **Strategy/Vision thinkers**: Lead with the big picture impact. They need to understand the \"why.\"\n- **Operations/Execution thinkers**: Be specific and action-oriented. Give them clear next steps.\n- **Relationships/Culture thinkers**: Protect the relationship. They may take feedback personally — extra care needed.\n\n**Do you want me to:**\n1. Draft talking points based on the specific person's profile?\n2. Help you practice the conversation?\n3. Suggest timing and setting for the conversation?"
      }
    ]
  }
];

// ================================================
// TEAM COMPOSITION SCENARIOS
// ================================================
const TEAM_SCENARIOS = [
  {
    id: 'product_launch',
    title: 'Product Launch Team',
    description: 'Cross-functional team for a major product launch',
    requiredLanes: ['vision', 'strategy', 'creative', 'marketing', 'execution'],
    requiredLevels: { min: 'L2', ideal: 'L3' },
    teamSize: 5,
    duration: '12 weeks'
  },
  {
    id: 'crisis_response',
    title: 'Crisis Response Team',
    description: 'Rapid response team for urgent organizational issue',
    requiredLanes: ['operations', 'execution', 'relationships'],
    requiredLevels: { min: 'L3', ideal: 'L4' },
    teamSize: 3,
    duration: '2-4 weeks'
  },
  {
    id: 'strategic_planning',
    title: 'Strategic Planning Team',
    description: 'Annual strategy development and planning',
    requiredLanes: ['vision', 'strategy', 'finance'],
    requiredLevels: { min: 'L3', ideal: 'L4' },
    teamSize: 4,
    duration: '8 weeks'
  },
  {
    id: 'culture_initiative',
    title: 'Culture Initiative Team',
    description: 'Team to drive organizational culture improvements',
    requiredLanes: ['culture', 'relationships', 'creative'],
    requiredLevels: { min: 'L2', ideal: 'L3' },
    teamSize: 4,
    duration: '6 months'
  },
  {
    id: 'coverage_backup',
    title: 'Coverage Planning',
    description: 'Find backup coverage for someone on extended leave',
    requiredLanes: [],
    requiredLevels: { min: 'L2', ideal: 'L3' },
    teamSize: 1,
    duration: '4-8 weeks'
  },
  {
    id: 'revenue_growth',
    title: 'Revenue Growth Task Force',
    description: 'Build and execute Q3 revenue acceleration strategy',
    requiredLanes: ['revenue', 'marketing', 'relationships', 'strategy'],
    requiredLevels: { min: 'L3', ideal: 'L4' },
    teamSize: 4,
    duration: '10 weeks'
  },
  {
    id: 'customer_experience',
    title: 'Customer Experience Squad',
    description: 'Redesign end-to-end customer journey',
    requiredLanes: ['relationships', 'creative', 'operations', 'marketing'],
    requiredLevels: { min: 'L2', ideal: 'L3' },
    teamSize: 4,
    duration: '16 weeks'
  },
  {
    id: 'tech_debt',
    title: 'Technical Debt Sprint',
    description: 'Dedicated team to tackle critical infrastructure improvements',
    requiredLanes: ['execution', 'operations', 'strategy'],
    requiredLevels: { min: 'L3', ideal: 'L3' },
    teamSize: 3,
    duration: '6 weeks'
  },
  {
    id: 'partnership_deal',
    title: 'Strategic Partnership Team',
    description: 'Negotiate and close major partnership opportunity',
    requiredLanes: ['relationships', 'revenue', 'finance', 'vision'],
    requiredLevels: { min: 'L4', ideal: 'L4' },
    teamSize: 4,
    duration: '8 weeks'
  },
  {
    id: 'innovation_lab',
    title: 'Innovation Sprint Team',
    description: 'Explore and prototype new product concepts',
    requiredLanes: ['vision', 'creative', 'execution', 'marketing'],
    requiredLevels: { min: 'L3', ideal: 'L4' },
    teamSize: 4,
    duration: '4 weeks'
  }
];

// ================================================
// SIMULATION SCENARIOS
// ================================================
const SIMULATION_SCENARIOS = [
  {
    id: 'key_departure',
    title: 'Key Person Departure',
    description: 'What if our top Revenue person leaves?',
    type: 'departure',
    icon: UserMinus,
    color: AI_COLORS.amber,
    borderColor: AI_COLORS.amber,
    isRisk: true
  },
  {
    id: 'promotion_wave',
    title: 'Promotion Wave',
    description: 'What if we promote 3 L3s to L4 next quarter?',
    type: 'promotion',
    icon: ArrowUpRight,
    color: AI_COLORS.primary,
    borderColor: AI_COLORS.primary,
    isRisk: false
  },
  {
    id: 'hiring_plan',
    title: 'Hiring Plan Impact',
    description: 'What if we hire 2 Creative lane thinkers?',
    type: 'hiring',
    icon: UserPlus,
    color: AI_COLORS.primary,
    borderColor: AI_COLORS.primary,
    isRisk: false
  },
  {
    id: 'reorg',
    title: 'Reorganization',
    description: 'What if we merge Marketing and Creative teams?',
    type: 'reorg',
    icon: Shuffle,
    color: AI_COLORS.purple,
    borderColor: AI_COLORS.amber,
    isRisk: true
  },
  {
    id: 'scale_50',
    title: 'Scale 50%',
    description: 'Where should we hire to scale the org by 50%?',
    type: 'scale',
    icon: TrendingUp,
    color: AI_COLORS.primary,
    borderColor: AI_COLORS.primary,
    isRisk: false
  },
  {
    id: 'budget_cut',
    title: 'Budget Reduction',
    description: 'What if we need to reduce headcount by 10%?',
    type: 'budget_cut',
    icon: AlertTriangle,
    color: '#EF4444',
    borderColor: AI_COLORS.amber,
    isRisk: true
  },
  {
    id: 'new_division',
    title: 'New Division Launch',
    description: 'What if we spin up a new product division?',
    type: 'new_division',
    icon: Plus,
    color: AI_COLORS.primary,
    borderColor: AI_COLORS.primary,
    isRisk: false
  },
  {
    id: 'remote_transition',
    title: 'Remote Transition',
    description: 'What if we shift to fully remote work?',
    type: 'remote',
    icon: Shuffle,
    color: AI_COLORS.blue,
    borderColor: AI_COLORS.amber,
    isRisk: true
  },
  {
    id: 'acquisition',
    title: 'Acquisition Integration',
    description: 'What if we acquire a 15-person team?',
    type: 'acquisition',
    icon: Users,
    color: AI_COLORS.purple,
    borderColor: AI_COLORS.primary,
    isRisk: false
  },
  {
    id: 'leadership_change',
    title: 'Leadership Change',
    description: 'What if we replace our Head of Product?',
    type: 'leadership',
    icon: Star,
    color: AI_COLORS.amber,
    borderColor: AI_COLORS.amber,
    isRisk: true
  },
  {
    id: 'soul_food_expansion',
    title: 'Soul Food Expansion',
    description: 'What if Soul Food Division doubles in size next quarter?',
    type: 'soul_food',
    icon: TrendingUp,
    color: AI_COLORS.soulFood,
    borderColor: AI_COLORS.soulFood,
    isRisk: false
  },
  {
    id: 'strategy_gap',
    title: 'Cognitive Lane Gap',
    description: 'What if we lose our only Strategy & Architecture thinker?',
    type: 'strategy_gap',
    icon: AlertTriangle,
    color: '#EF4444',
    borderColor: AI_COLORS.amber,
    isRisk: true
  },
  {
    id: 'cos_transition',
    title: 'Chief of Staff Transition',
    description: 'What if the Chief of Staff role changes hands?',
    type: 'cos_transition',
    icon: Shuffle,
    color: AI_COLORS.purple,
    borderColor: AI_COLORS.purple,
    isRisk: true
  }
];

// ================================================
// COACHING CHAT COMPONENT
// ================================================
const CoachingChat = ({ scenario, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationIndex, setConversationIndex] = useState(0);

  useEffect(() => {
    // Load initial conversation
    if (scenario?.conversation) {
      setMessages([scenario.conversation[0]]);
      // Simulate AI typing first response
      setIsTyping(true);
      setTimeout(() => {
        setMessages(prev => [...prev, scenario.conversation[1]]);
        setIsTyping(false);
        setConversationIndex(2);
      }, 1500);
    }
  }, [scenario]);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    
    // Add user message
    const userMsg = { role: 'user', content: inputValue };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    
    // Simulate AI response
    setIsTyping(true);
    setTimeout(() => {
      if (scenario?.conversation && conversationIndex < scenario.conversation.length) {
        setMessages(prev => [...prev, scenario.conversation[conversationIndex + 1] || {
          role: 'ai',
          content: "I understand. Based on your cognitive profile and the patterns I'm seeing, I'd recommend we take a structured approach to address this. Would you like me to create an action plan, or would you prefer to explore this further through conversation?"
        }]);
        setConversationIndex(prev => prev + 2);
      } else {
        setMessages(prev => [...prev, {
          role: 'ai',
          content: "That's a great follow-up question. Based on the data I'm seeing in your profile, I'd suggest we approach this systematically. Your cognitive strengths in " + (COGNITIVE_LANES[Math.floor(Math.random() * 10)]?.name || 'Strategy') + " give you a natural advantage here. Would you like me to break down specific next steps?"
        }]);
      }
      setIsTyping(false);
    }, 1500);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-gray-100">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 rotate-180 text-gray-500" />
        </button>
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${scenario?.color}15` }}
        >
          {scenario?.icon && <scenario.icon className="w-5 h-5" style={{ color: scenario?.color }} />}
        </div>
        <div>
          <h3 className="font-bold text-gray-900">{scenario?.title}</h3>
          <p className="text-sm text-gray-500">{scenario?.description}</p>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div 
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-[80%] p-4 rounded-2xl ${
                msg.role === 'user' 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-white border border-gray-100 shadow-sm'
              }`}
            >
              {msg.role === 'ai' && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-teal-600">Cognitive Coach</span>
                </div>
              )}
              <div className={`text-sm leading-relaxed whitespace-pre-wrap ${msg.role === 'ai' ? 'text-gray-700' : ''}`}>
                {msg.content}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-100 shadow-sm p-4 rounded-2xl">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Input */}
      <div className="p-4 border-t border-gray-100">
        <div className="flex gap-3">
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSend()}
            placeholder="Ask a follow-up question..."
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className="px-4 py-3 rounded-xl bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================
// COACHING TAB
// ================================================
const CoachingTab = () => {
  const [selectedScenario, setSelectedScenario] = useState(null);
  
  if (selectedScenario) {
    return <CoachingChat scenario={selectedScenario} onBack={() => setSelectedScenario(null)} />;
  }
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Cognitive Coaching AI</h2>
        <p className="text-gray-500">
          Get personalized coaching based on your cognitive profile and work patterns.
        </p>
      </div>
      
      {/* Quick Start */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Start a Conversation
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {COACHING_SCENARIOS.map(scenario => (
            <button
              key={scenario.id}
              onClick={() => setSelectedScenario(scenario)}
              className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all text-left group"
              data-testid={`coaching-scenario-${scenario.id}`}
            >
              <div className="flex items-start gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${scenario.color}15` }}
                >
                  <scenario.icon className="w-6 h-6" style={{ color: scenario.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {scenario.title}
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    {scenario.description}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-teal-500 transition-colors flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================================================
// TEAM OPTIMIZER TAB
// ================================================
const TeamOptimizerTab = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [optimizedTeam, setOptimizedTeam] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [savedComposition, setSavedComposition] = useState(false);
  
  const optimizeTeam = (project) => {
    setSelectedProject(project);
    setIsOptimizing(true);
    setSavedComposition(false);
    
    setTimeout(() => {
      // Generate optimized team based on project requirements
      const team = [];
      const usedIds = new Set();
      const coveredLanes = [];
      
      project.requiredLanes.forEach(laneId => {
        const lane = COGNITIVE_LANES.find(l => l.id === laneId);
        const candidates = teamMembers.filter(m => 
          m.lane_proficiency?.[laneId] && 
          !usedIds.has(m.id) &&
          (m.current_load / m.max_task_load) < 0.8
        );
        
        if (candidates.length > 0) {
          const best = candidates.sort((a, b) => {
            const aLevel = parseInt((a.lane_proficiency[laneId] || 'L1').replace('L', ''));
            const bLevel = parseInt((b.lane_proficiency[laneId] || 'L1').replace('L', ''));
            return bLevel - aLevel;
          })[0];
          
          team.push({
            member: best,
            lane,
            laneId,
            role: `${lane.name} Lead`,
            matchScore: 85 + Math.floor(Math.random() * 12),
            availability: 100 - Math.round((best.current_load / best.max_task_load) * 100)
          });
          usedIds.add(best.id);
          coveredLanes.push(laneId);
        }
      });
      
      const missingLanes = project.requiredLanes.filter(l => !coveredLanes.includes(l));
      
      setOptimizedTeam({
        project,
        team,
        coveredLanes,
        missingLanes,
        overallScore: Math.round(team.reduce((acc, t) => acc + t.matchScore, 0) / Math.max(team.length, 1)),
        cognitiveBalance: missingLanes.length === 0 ? 'Full Coverage' : 'Coverage Gap',
        riskFactors: missingLanes.map(l => {
          const lane = COGNITIVE_LANES.find(x => x.id === l);
          return `Missing ${lane?.name} coverage`;
        }),
        recommendation: missingLanes.length === 0
          ? `This team composition provides strong cognitive coverage for ${project.title}. The mix of ${team.map(t => t.lane.name).join(', ')} lanes creates a balanced approach.`
          : `This team has strong coverage but is missing ${missingLanes.map(l => COGNITIVE_LANES.find(x => x.id === l)?.name).join(', ')} capacity. Consider adding team members with these lane strengths.`
      });
      setIsOptimizing(false);
    }, 1500);
  };
  
  const handleBuildTeam = () => {
    // Mock - would create a new Collab with these members
    alert(`Creating new Collab: "${optimizedTeam.project.title}" with ${optimizedTeam.team.map(t => t.member.name).join(', ')}`);
  };
  
  const handleSaveComposition = () => {
    setSavedComposition(true);
    setTimeout(() => setSavedComposition(false), 3000);
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Composition Optimizer</h2>
        <p className="text-gray-500">
          Build the ideal team for any project based on cognitive lane requirements.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Selection */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Select Project Type
          </h3>
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
            {TEAM_SCENARIOS.map(scenario => (
              <button
                key={scenario.id}
                onClick={() => optimizeTeam(scenario)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedProject?.id === scenario.id 
                    ? 'border-teal-500 bg-teal-50' 
                    : 'border-gray-100 hover:border-gray-200 bg-white'
                }`}
                data-testid={`project-${scenario.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{scenario.title}</h4>
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                    {scenario.duration}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-3">{scenario.description}</p>
                <div className="flex flex-wrap gap-2">
                  {scenario.requiredLanes.map(laneId => {
                    const lane = COGNITIVE_LANES.find(l => l.id === laneId);
                    return (
                      <span 
                        key={laneId}
                        className="text-xs px-2 py-1 rounded-full"
                        style={{ backgroundColor: `${lane?.color}15`, color: lane?.color }}
                      >
                        {lane?.name}
                      </span>
                    );
                  })}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Optimized Team Results */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Optimized Team
          </h3>
          
          {isOptimizing ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 flex flex-col items-center justify-center">
              <RefreshCw className="w-8 h-8 text-teal-500 animate-spin mb-4" />
              <p className="text-gray-500">Analyzing cognitive profiles...</p>
            </div>
          ) : optimizedTeam ? (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="optimized-team">
              {/* Header */}
              <div className="p-4 bg-gradient-to-r from-teal-500 to-emerald-500">
                <div className="flex items-center justify-between text-white">
                  <div>
                    <h4 className="font-bold">{optimizedTeam.project.title}</h4>
                    <p className="text-sm opacity-80">{optimizedTeam.team.length} team members</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-black">{optimizedTeam.overallScore}%</p>
                    <p className="text-xs opacity-80">Match Score</p>
                  </div>
                </div>
              </div>
              
              {/* Team Members with Lane Color Borders */}
              <div className="p-4 space-y-3">
                {optimizedTeam.team.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50"
                    style={{ borderLeft: `4px solid ${item.lane.color}` }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: item.lane.color }}
                    >
                      {item.member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{item.member.name}</p>
                      <p className="text-xs text-gray-500">{item.role}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-teal-600">{item.matchScore}%</p>
                      <p className="text-xs text-gray-400">{item.availability}% available</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Team Coverage Bar */}
              <div className="px-4 pb-4">
                <div className="p-3 rounded-xl bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Team Coverage</span>
                    <span 
                      className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        optimizedTeam.missingLanes.length === 0 
                          ? 'bg-teal-100 text-teal-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {optimizedTeam.missingLanes.length === 0 ? 'Full Coverage' : `Missing: ${optimizedTeam.missingLanes.map(l => COGNITIVE_LANES.find(x => x.id === l)?.name).join(', ')}`}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {optimizedTeam.project.requiredLanes.map(laneId => {
                      const lane = COGNITIVE_LANES.find(l => l.id === laneId);
                      const isCovered = optimizedTeam.coveredLanes.includes(laneId);
                      return (
                        <div 
                          key={laneId}
                          className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                            isCovered ? '' : 'border-2'
                          }`}
                          style={{ 
                            backgroundColor: isCovered ? lane?.color : 'transparent',
                            borderColor: !isCovered ? lane?.color : 'transparent'
                          }}
                          title={`${lane?.name} ${isCovered ? '(covered)' : '(missing)'}`}
                        >
                          {isCovered && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* Recommendation */}
              <div className="px-4 pb-4">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-teal-50">
                  <Sparkles className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-teal-800">{optimizedTeam.recommendation}</p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="p-4 border-t border-gray-100 flex gap-3">
                <button
                  onClick={handleBuildTeam}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: AI_COLORS.primary }}
                >
                  <Plus className="w-4 h-4" />
                  Build This Team
                </button>
                <button
                  onClick={handleSaveComposition}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold transition-all ${
                    savedComposition 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {savedComposition ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Star className="w-4 h-4" />
                      Save Composition
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 flex flex-col items-center justify-center text-center">
              <Users className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500">Select a project type to see the optimized team composition</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ================================================
// ORG SIMULATION TAB
// ================================================
const OrgSimulationTab = () => {
  const [selectedScenario, setSelectedScenario] = useState(SIMULATION_SCENARIOS[0]); // Pre-select first
  const [simulationResult, setSimulationResult] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  
  // Pre-load first scenario on mount
  useEffect(() => {
    if (!simulationResult && selectedScenario) {
      runSimulation(selectedScenario);
    }
  }, []);
  
  const runSimulation = (scenario) => {
    setSelectedScenario(scenario);
    setIsSimulating(true);
    
    setTimeout(() => {
      let result = {};
      
      switch(scenario.type) {
        case 'departure':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 62,
            change: -23,
            impacts: [
              { area: 'Revenue Lane', impact: 'Critical', detail: 'Single point of failure exposed. No backup coverage.' },
              { area: 'Client Relationships', impact: 'High', detail: '12 key accounts lose primary contact' },
              { area: 'Team Morale', impact: 'Medium', detail: 'Top performer departure signals instability' }
            ],
            highestRisk: ['Aisha Patel (Revenue)', 'Key client accounts Q1-Q3'],
            recommendations: [
              'Immediately begin succession planning for Revenue lane',
              'Cross-train Marcus Rivera (shows L2 Revenue aptitude)',
              'Document critical client relationships and knowledge'
            ],
            timeline: 'If departure occurs: 6-8 weeks to stabilize, 3-4 months to recover'
          };
          break;
        case 'promotion':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 91,
            change: +6,
            impacts: [
              { area: 'Leadership Density', impact: 'Positive', detail: 'L4 coverage increases from 4 to 7' },
              { area: 'L3 Layer', impact: 'Watch', detail: 'Gap in L3 operational capacity' },
              { area: 'Decision Speed', impact: 'Positive', detail: 'More distributed decision-making' }
            ],
            highestRisk: ['L3 bench depth may thin', 'Promotion candidates need clear scope'],
            recommendations: [
              'Backfill L3 positions with targeted development',
              'Ensure promoted individuals have clear scope boundaries',
              'Plan 90-day transition support for each promotion'
            ],
            timeline: 'Positive impact visible within 30 days, full integration in 90 days'
          };
          break;
        case 'hiring':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 89,
            change: +4,
            impacts: [
              { area: 'Creative Lane', impact: 'Resolved', detail: 'Previously thin lane now has L3 coverage' },
              { area: 'Onboarding Load', impact: 'Medium Risk', detail: 'Operations team absorbs integration work' },
              { area: 'Culture', impact: 'Watch', detail: 'New hires need culture integration time' }
            ],
            highestRisk: ['David Park (Operations) at 92% capacity', 'Onboarding bandwidth'],
            recommendations: [
              'Stagger hiring over 2 sprints to manage onboarding load',
              'Assign culture buddies from Relationships lane',
              'Clear first-90-day objectives for new hires'
            ],
            timeline: 'Full productivity expected in 60-90 days per hire'
          };
          break;
        case 'reorg':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 78,
            change: -7,
            impacts: [
              { area: 'Role Clarity', impact: 'High', detail: 'Reporting lines become unclear during transition' },
              { area: 'Creative Output', impact: 'Medium', detail: 'Creative work may slow during integration' },
              { area: 'Marketing Velocity', impact: 'Watch', detail: 'Campaign delivery may be affected' }
            ],
            highestRisk: ['Emma Thompson (Creative)', 'David Kim (Marketing)', 'Active campaigns'],
            recommendations: [
              'Define clear decision rights before announcing',
              'Maintain separate workflows for 30 days during transition',
              'Appoint integration lead from Operations lane'
            ],
            timeline: 'Transition: 30-45 days; Stabilization: 60-90 days'
          };
          break;
        case 'scale':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 88,
            change: +3,
            impacts: [
              { area: 'Operations Lane', impact: 'Priority', detail: 'Hire 3 Ops L2-L3 to support scale' },
              { area: 'Creative Lane', impact: 'Priority', detail: 'Hire 2 Creative L3 to resolve thin lane' },
              { area: 'Leadership', impact: 'Watch', detail: 'May need additional L4 capacity' }
            ],
            highestRisk: ['Current Operations at capacity', 'Onboarding infrastructure'],
            recommendations: [
              'Prioritize: 2 Creative, 3 Operations, 1 Strategy',
              'Hire Operations first to support onboarding capacity',
              'Consider promoting internally before external hires'
            ],
            timeline: 'Phased hiring over 6 months recommended'
          };
          break;
        case 'budget_cut':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 71,
            change: -14,
            impacts: [
              { area: 'Creative Lane', impact: 'Critical', detail: 'Already thin — any reduction creates gap' },
              { area: 'Team Morale', impact: 'High', detail: 'Layoffs impact remaining team confidence' },
              { area: 'Execution Capacity', impact: 'Medium', detail: 'Remaining team absorbs more load' }
            ],
            highestRisk: ['Creative lane (only 2 people)', 'Team trust and engagement'],
            recommendations: [
              'Protect core lane coverage — cut across roles, not entire lanes',
              'Communicate transparently about business rationale',
              'Increase coaching support for remaining team'
            ],
            timeline: 'Immediate impact; 3-4 months to stabilize morale'
          };
          break;
        case 'new_division':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 79,
            change: -6,
            impacts: [
              { area: 'Leadership Stretch', impact: 'High', detail: 'Existing L4s split attention across divisions' },
              { area: 'Knowledge Transfer', impact: 'Medium', detail: 'New team needs institutional knowledge ramp' },
              { area: 'Culture Dilution', impact: 'Watch', detail: 'New division develops own subculture' }
            ],
            highestRisk: ['Sarah Chen (Vision L4)', 'Current project timelines'],
            recommendations: [
              'Staff new division from internal promotions where possible',
              'Assign dedicated L4 leader — avoid dual-reporting',
              'Create cross-division rituals to maintain culture'
            ],
            timeline: '6 months to establish, 12 months to full productivity'
          };
          break;
        case 'remote':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 82,
            change: -3,
            impacts: [
              { area: 'Culture Lane', impact: 'High', detail: 'Informal culture transmission weakens' },
              { area: 'Relationships Lane', impact: 'Medium', detail: 'Cross-team collaboration requires more intentionality' },
              { area: 'Operations', impact: 'Positive', detail: 'Async processes may improve efficiency' }
            ],
            highestRisk: ['Jennifer Lee (Culture L4)', 'New hire onboarding quality'],
            recommendations: [
              'Invest in Culture lane programming — virtual rituals, offsites',
              'Over-communicate in writing — reduce "hallway decision" dependency',
              'Create structured mentorship to replace informal learning'
            ],
            timeline: 'Adjustment period: 2-3 months'
          };
          break;
        case 'acquisition':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 76,
            change: -9,
            impacts: [
              { area: 'Culture Integration', impact: 'High', detail: 'Two team cultures must merge' },
              { area: 'Duplicate Roles', impact: 'Medium', detail: 'Overlapping positions create ambiguity' },
              { area: 'Cognitive Diversity', impact: 'Positive', detail: '+5 new lane perspectives' }
            ],
            highestRisk: ['Culture alignment', 'Role redundancy concerns'],
            recommendations: [
              'Map acquired team cognitive profiles immediately',
              'Identify unique capabilities to preserve',
              'Create integration buddy system'
            ],
            timeline: 'Integration: 4-6 months; Full productivity: 9-12 months'
          };
          break;
        case 'leadership':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 73,
            change: -12,
            impacts: [
              { area: 'Strategy Lane', impact: 'Critical', detail: 'L4 gap in critical decision-making' },
              { area: 'Team Stability', impact: 'High', detail: 'Direct reports face uncertainty' },
              { area: 'Institutional Knowledge', impact: 'High', detail: 'Years of context walks out the door' }
            ],
            highestRisk: ['Sarah Chen direct reports', 'Active strategic initiatives'],
            recommendations: [
              'Document critical decisions and rationale before transition',
              'Identify internal succession candidates immediately',
              'Consider overlap period for knowledge transfer'
            ],
            timeline: 'Minimum 2-month transition; full recovery 4-6 months'
          };
          break;
        case 'soul_food':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 81,
            change: -4,
            impacts: [
              { area: 'Operations Lane', impact: 'High', detail: 'Soul Food ops team stretched thin with 2x volume' },
              { area: 'Executive Attention', impact: 'Medium', detail: 'Leadership bandwidth split across growing division' },
              { area: 'Revenue Growth', impact: 'Positive', detail: 'Significant revenue upside if executed well' }
            ],
            highestRisk: ['Soul Food Operations capacity', 'Cross-division resource competition'],
            recommendations: [
              'Hire dedicated Operations L3 for Soul Food before expansion',
              'Create clear resource allocation boundaries between divisions',
              'Establish Soul Food-specific KPIs and reporting cadence'
            ],
            timeline: 'Preparation: 6-8 weeks before expansion; Full scale: 4 months'
          };
          break;
        case 'strategy_gap':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 58,
            change: -27,
            impacts: [
              { area: 'Strategy & Architecture', impact: 'Critical', detail: 'Complete L4 gap — no one owns strategic decisions' },
              { area: 'Vision Lane', impact: 'High', detail: 'Vision work loses strategic translation layer' },
              { area: 'Execution Quality', impact: 'High', detail: 'Work may lack strategic coherence' }
            ],
            highestRisk: ['James Liu (Strategy L4)', 'All active strategic initiatives'],
            recommendations: [
              'Emergency succession planning — identify internal L3 candidates',
              'Consider interim external Strategy consultant',
              'Document all active strategic frameworks and decisions'
            ],
            timeline: 'Critical gap within 2 weeks; Recovery 4-6 months minimum'
          };
          break;
        case 'cos_transition':
          result = {
            scenario,
            currentHealth: 85,
            projectedHealth: 72,
            change: -13,
            impacts: [
              { area: 'Executive Coordination', impact: 'Critical', detail: 'Central coordination point disrupted' },
              { area: 'Information Flow', impact: 'High', detail: 'Cross-functional visibility temporarily lost' },
              { area: 'Team Morale', impact: 'Medium', detail: 'Chief of Staff is often the "glue" between teams' }
            ],
            highestRisk: ['Active executive initiatives', 'Cross-team coordination'],
            recommendations: [
              'Document all active initiatives and stakeholder relationships',
              'Identify interim coordination owner from Operations',
              'Plan minimum 30-day overlap for knowledge transfer'
            ],
            timeline: 'Transition: 4-6 weeks; New CoS ramp: 60-90 days'
          };
          break;
        default:
          result = { scenario, currentHealth: 85, projectedHealth: 85, change: 0, impacts: [], highestRisk: [], recommendations: [], timeline: 'No significant impact projected' };
      }
      
      setSimulationResult(result);
      setIsSimulating(false);
    }, selectedScenario === scenario ? 0 : 1500); // Instant for pre-load, delayed for user clicks
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Organizational Simulation</h2>
        <p className="text-gray-500">
          Model "what if" scenarios to see projected impact on organizational cognitive health.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scenario Selection */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Select Scenario
          </h3>
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
            {SIMULATION_SCENARIOS.map(scenario => (
              <button
                key={scenario.id}
                onClick={() => runSimulation(scenario)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedScenario?.id === scenario.id 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-100 hover:border-gray-200 bg-white'
                }`}
                style={{ 
                  borderLeftWidth: '4px',
                  borderLeftColor: scenario.borderColor || (scenario.isRisk ? AI_COLORS.amber : AI_COLORS.primary)
                }}
                data-testid={`simulation-${scenario.id}`}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${scenario.color}15` }}
                  >
                    <scenario.icon className="w-6 h-6" style={{ color: scenario.color }} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{scenario.title}</h4>
                    <p className="text-sm text-gray-500">{scenario.description}</p>
                  </div>
                  {scenario.isRisk ? (
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  ) : (
                    <TrendingUp className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Simulation Results */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Simulation Results
          </h3>
          
          {isSimulating ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 flex flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
                <Brain className="w-6 h-6 text-purple-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="text-gray-500 mt-4">Running simulation...</p>
            </div>
          ) : simulationResult ? (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden max-h-[600px] overflow-y-auto" data-testid="simulation-result">
              {/* Health Score Comparison */}
              <div className="p-5 bg-gradient-to-r from-gray-900 to-gray-800">
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Current</p>
                    <p className="text-3xl font-black text-white">{simulationResult.currentHealth}</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center px-4">
                    {simulationResult.change >= 0 ? (
                      <ArrowUpRight className="w-8 h-8 text-emerald-400" />
                    ) : (
                      <div className="transform rotate-45">
                        <ArrowUpRight className="w-8 h-8 text-red-400" style={{ transform: 'rotate(90deg)' }} />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Projected</p>
                    <p 
                      className="text-3xl font-black"
                      style={{ color: simulationResult.change >= 0 ? '#10B981' : '#EF4444' }}
                    >
                      {simulationResult.projectedHealth}
                    </p>
                  </div>
                  <div className="ml-4 text-center">
                    <p 
                      className={`text-2xl font-bold ${simulationResult.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                    >
                      {simulationResult.change >= 0 ? '+' : ''}{simulationResult.change}
                    </p>
                    <p className="text-xs text-gray-400">Change</p>
                  </div>
                </div>
              </div>
              
              {/* Projected Impact */}
              <div className="p-4 border-b border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-purple-500" />
                  Projected Impact
                </h4>
                <div className="space-y-2">
                  {simulationResult.impacts.map((impact, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-gray-50">
                      <div 
                        className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                        style={{ 
                          backgroundColor: impact.impact === 'Critical' ? '#EF4444' :
                                          impact.impact === 'High' || impact.impact === 'High Risk' ? '#F59E0B' :
                                          impact.impact === 'Positive' || impact.impact === 'Resolved' ? '#10B981' :
                                          '#94A3B8'
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 text-sm">{impact.area}</span>
                          <span 
                            className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ 
                              backgroundColor: impact.impact === 'Critical' ? '#FEE2E2' :
                                              impact.impact === 'High' || impact.impact === 'High Risk' ? '#FEF3C7' :
                                              impact.impact === 'Positive' || impact.impact === 'Resolved' ? '#D1FAE5' :
                                              '#F3F4F6',
                              color: impact.impact === 'Critical' ? '#DC2626' :
                                    impact.impact === 'High' || impact.impact === 'High Risk' ? '#D97706' :
                                    impact.impact === 'Positive' || impact.impact === 'Resolved' ? '#059669' :
                                    '#6B7280'
                            }}
                          >
                            {impact.impact}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{impact.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Highest Risk Areas */}
              {simulationResult.highestRisk && simulationResult.highestRisk.length > 0 && (
                <div className="p-4 border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Highest Risk Areas
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {simulationResult.highestRisk.map((risk, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                      >
                        {risk}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recommended Actions */}
              <div className="p-4 border-b border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-teal-500" />
                  Recommended Actions
                </h4>
                <ul className="space-y-2">
                  {simulationResult.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-teal-700">{idx + 1}</span>
                      </div>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Timeline */}
              <div className="p-4 bg-gray-50">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500">Timeline:</span>
                  <span className="text-xs text-gray-700">{simulationResult.timeline}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 flex flex-col items-center justify-center text-center">
              <BarChart3 className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500">Select a scenario to run a simulation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ================================================
// FRICTION ARCHETYPES
// ================================================
const FRICTION_PATTERNS = [
  {
    id: 'pace_clash',
    name: 'Pace Clash',
    description: 'Vision thinkers want to explore possibilities while Execution thinkers want to act now.',
    lanes: ['vision', 'execution'],
    symptoms: ['Meetings feel unproductive to both parties', 'Execution feels rushed into unclear direction', 'Vision feels blocked by "just do it" mentality'],
    bridges: ['Establish "explore then execute" time blocks', 'Vision: Share the headline first, details after', 'Execution: Ask "what problem are we solving?" before acting']
  },
  {
    id: 'detail_vs_big_picture',
    name: 'Detail vs Big Picture',
    description: 'Operations focuses on how things work while Strategy focuses on why they matter.',
    lanes: ['operations', 'strategy'],
    symptoms: ['Operations feels strategy is "out of touch"', 'Strategy feels operations is "missing the point"', 'Handoffs get lost in translation'],
    bridges: ['Operations: Lead with "Here\'s how this connects to the goal"', 'Strategy: End with "Here\'s what I need from you specifically"', 'Create shared documentation that covers both levels']
  },
  {
    id: 'process_vs_creativity',
    name: 'Process vs Creativity',
    description: 'Creative wants freedom to explore while Operations wants predictable systems.',
    lanes: ['creative', 'operations'],
    symptoms: ['Creative feels micromanaged', 'Operations feels creative is chaotic', 'Deadlines become points of tension'],
    bridges: ['Define "creative sandbox" with clear boundaries', 'Operations: Trust the process, check outcomes not steps', 'Creative: Communicate blockers early, not at deadline']
  },
  {
    id: 'relationship_vs_results',
    name: 'Relationship vs Results',
    description: 'Relationships prioritizes connection while Revenue prioritizes outcomes.',
    lanes: ['relationships', 'revenue'],
    symptoms: ['Revenue feels relationships is "too soft"', 'Relationships feels revenue is "pushy"', 'Client handoffs create friction'],
    bridges: ['Acknowledge both are essential to long-term success', 'Revenue: Trust relationship context before pushing', 'Relationships: Frame connection in terms of business value']
  },
  {
    id: 'l4_ownership',
    name: 'L4 Territory Overlap',
    description: 'Two L4 leaders in adjacent lanes may compete for strategic ownership.',
    lanes: ['any'],
    symptoms: ['Decisions get relitigated', 'Direct reports get conflicting direction', 'Strategic alignment meetings become debates'],
    bridges: ['Clearly define decision rights upfront', 'Establish "tie-breaker" escalation path', 'Regular 1:1s between L4s to align before team meetings']
  },
  {
    id: 'culture_vs_execution',
    name: 'Culture vs Execution',
    description: 'Culture wants to protect team wellbeing while Execution wants to hit targets.',
    lanes: ['culture', 'execution'],
    symptoms: ['Culture feels execution is "burning out the team"', 'Execution feels culture is "slowing things down"', 'Workload discussions become adversarial'],
    bridges: ['Frame culture as enabler of sustainable execution', 'Execution: Include "team health" as a success metric', 'Culture: Provide solutions, not just concerns']
  }
];

// ================================================
// CHEMISTRY CALCULATION
// ================================================
const calculateChemistry = (member1, member2) => {
  // Base score starts at 70 (neutral chemistry)
  let score = 70;
  let factors = [];
  let frictionRisks = [];
  let strengths = [];
  
  const lanes1 = Object.keys(member1.lane_proficiency || {});
  const lanes2 = Object.keys(member2.lane_proficiency || {});
  
  // 1. Complementary lanes (different lanes = good for collaboration)
  const uniqueLanes1 = lanes1.filter(l => !lanes2.includes(l));
  const uniqueLanes2 = lanes2.filter(l => !lanes1.includes(l));
  const complementaryCount = uniqueLanes1.length + uniqueLanes2.length;
  
  if (complementaryCount >= 4) {
    score += 15;
    strengths.push('Strong complementary skills — they cover different cognitive lanes');
  } else if (complementaryCount >= 2) {
    score += 8;
    strengths.push('Good skill diversity — some complementary lanes');
  }
  
  // 2. Shared lanes (some overlap is good for communication)
  const sharedLanes = lanes1.filter(l => lanes2.includes(l));
  if (sharedLanes.length >= 2) {
    score += 10;
    strengths.push('Shared language — they understand each other\'s primary domains');
  } else if (sharedLanes.length === 1) {
    score += 5;
    factors.push({ type: 'neutral', text: 'Limited overlap — may need to work harder to communicate' });
  } else {
    score -= 5;
    factors.push({ type: 'risk', text: 'No shared lanes — high translation effort required' });
  }
  
  // 3. Check for known friction patterns
  FRICTION_PATTERNS.forEach(pattern => {
    if (pattern.lanes[0] === 'any') {
      // L4 overlap check
      const bothL4 = Object.values(member1.lane_proficiency || {}).includes('L4') && 
                     Object.values(member2.lane_proficiency || {}).includes('L4');
      const adjacentLanes = sharedLanes.length > 0;
      if (bothL4 && adjacentLanes) {
        score -= 10;
        frictionRisks.push(pattern);
      }
    } else {
      const hasLane1 = lanes1.includes(pattern.lanes[0]) || lanes1.includes(pattern.lanes[1]);
      const hasLane2 = lanes2.includes(pattern.lanes[0]) || lanes2.includes(pattern.lanes[1]);
      const differentLanes = (lanes1.includes(pattern.lanes[0]) && lanes2.includes(pattern.lanes[1])) ||
                            (lanes1.includes(pattern.lanes[1]) && lanes2.includes(pattern.lanes[0]));
      if (differentLanes) {
        score -= 8;
        frictionRisks.push(pattern);
      }
    }
  });
  
  // 4. Level compatibility
  const levels1 = Object.values(member1.lane_proficiency || {}).map(l => parseInt(l.replace('L', '')));
  const levels2 = Object.values(member2.lane_proficiency || {}).map(l => parseInt(l.replace('L', '')));
  const avgLevel1 = levels1.reduce((a, b) => a + b, 0) / levels1.length;
  const avgLevel2 = levels2.reduce((a, b) => a + b, 0) / levels2.length;
  const levelGap = Math.abs(avgLevel1 - avgLevel2);
  
  if (levelGap < 0.5) {
    score += 5;
    strengths.push('Similar seniority levels — peer collaboration works well');
  } else if (levelGap > 1.5) {
    factors.push({ type: 'neutral', text: 'Significant level gap — may need mentor/mentee framing' });
  }
  
  // 5. Capacity compatibility (both at similar capacity = easier to collaborate)
  const capacity1 = (member1.current_load || 5) / (member1.max_task_load || 10);
  const capacity2 = (member2.current_load || 5) / (member2.max_task_load || 10);
  if (Math.abs(capacity1 - capacity2) < 0.2) {
    score += 5;
    strengths.push('Similar availability — can dedicate comparable time');
  } else if (capacity1 > 0.85 || capacity2 > 0.85) {
    score -= 5;
    factors.push({ type: 'risk', text: 'One or both at high capacity — collaboration may strain resources' });
  }
  
  // Clamp score
  score = Math.max(40, Math.min(98, score));
  
  return {
    score,
    label: score >= 85 ? 'Excellent' : score >= 70 ? 'Good' : score >= 55 ? 'Fair' : 'Challenging',
    color: score >= 85 ? '#10B981' : score >= 70 ? '#00C9A7' : score >= 55 ? '#F59E0B' : '#EF4444',
    strengths,
    frictionRisks,
    factors
  };
};

// ================================================
// TEAM CHEMISTRY TAB
// ================================================
const TeamChemistryTab = () => {
  const [selectedPair, setSelectedPair] = useState(null);
  const [viewMode, setViewMode] = useState('matrix'); // matrix, pair, overview
  
  // Calculate overall team chemistry
  const teamChemistryScore = useMemo(() => {
    let totalScore = 0;
    let pairCount = 0;
    for (let i = 0; i < teamMembers.length; i++) {
      for (let j = i + 1; j < teamMembers.length; j++) {
        totalScore += calculateChemistry(teamMembers[i], teamMembers[j]).score;
        pairCount++;
      }
    }
    return Math.round(totalScore / pairCount);
  }, []);
  
  // Find top friction hotspots
  const frictionHotspots = useMemo(() => {
    const pairs = [];
    for (let i = 0; i < teamMembers.length; i++) {
      for (let j = i + 1; j < teamMembers.length; j++) {
        const chemistry = calculateChemistry(teamMembers[i], teamMembers[j]);
        if (chemistry.frictionRisks.length > 0) {
          pairs.push({
            member1: teamMembers[i],
            member2: teamMembers[j],
            chemistry,
            riskCount: chemistry.frictionRisks.length
          });
        }
      }
    }
    return pairs.sort((a, b) => a.chemistry.score - b.chemistry.score).slice(0, 5);
  }, []);
  
  // Find strongest pairs
  const strongestPairs = useMemo(() => {
    const pairs = [];
    for (let i = 0; i < teamMembers.length; i++) {
      for (let j = i + 1; j < teamMembers.length; j++) {
        const chemistry = calculateChemistry(teamMembers[i], teamMembers[j]);
        pairs.push({
          member1: teamMembers[i],
          member2: teamMembers[j],
          chemistry
        });
      }
    }
    return pairs.sort((a, b) => b.chemistry.score - a.chemistry.score).slice(0, 5);
  }, []);
  
  const handlePairClick = (member1, member2) => {
    setSelectedPair({ member1, member2, chemistry: calculateChemistry(member1, member2) });
    setViewMode('pair');
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Chemistry</h2>
        <p className="text-gray-500">
          Understand cognitive compatibility between team members and identify collaboration opportunities.
        </p>
      </div>
      
      {/* View Mode Toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setViewMode('overview'); setSelectedPair(null); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            viewMode === 'overview' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          style={{ backgroundColor: viewMode === 'overview' ? AI_COLORS.chemistry : undefined }}
        >
          Overview
        </button>
        <button
          onClick={() => { setViewMode('matrix'); setSelectedPair(null); }}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            viewMode === 'matrix' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          style={{ backgroundColor: viewMode === 'matrix' ? AI_COLORS.chemistry : undefined }}
        >
          Compatibility Matrix
        </button>
        <button
          onClick={() => setViewMode('pair')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            viewMode === 'pair' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          style={{ backgroundColor: viewMode === 'pair' ? AI_COLORS.chemistry : undefined }}
        >
          Pair Analysis
        </button>
      </div>
      
      {/* Overview View */}
      {viewMode === 'overview' && (
        <div className="space-y-6">
          {/* Team Score Card - Deep Rose-Purple */}
          <div 
            className="rounded-2xl p-6 text-white"
            style={{ background: 'linear-gradient(135deg, #1A0A1A 0%, #2D1535 100%)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-200 text-sm font-medium">Team Chemistry Score</p>
                <p 
                  className="text-5xl font-black mt-1"
                  style={{ color: AI_COLORS.chemistry }}
                >
                  {teamChemistryScore}%
                </p>
                <p className="text-pink-200/80 mt-2">
                  {teamChemistryScore >= 75 ? 'Strong team cohesion — complementary skills and shared understanding' :
                   teamChemistryScore >= 60 ? 'Good team dynamics — some areas for improvement' :
                   'Team may benefit from targeted collaboration coaching'}
                </p>
              </div>
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(194, 24, 91, 0.2)' }}
              >
                <HeartHandshake className="w-12 h-12" style={{ color: AI_COLORS.chemistry }} />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strongest Pairs */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Strongest Pairings
              </h3>
              <div className="space-y-3">
                {strongestPairs.map((pair, idx) => {
                  // Generate unique descriptions based on actual lane combinations
                  const lanes1 = Object.keys(pair.member1.lane_proficiency || {});
                  const lanes2 = Object.keys(pair.member2.lane_proficiency || {});
                  const uniqueDescriptions = {
                    'vision-creative': 'Big picture vision meets visual storytelling',
                    'creative-vision': 'Big picture vision meets visual storytelling',
                    'vision-relationships': 'Strategic vision paired with relationship depth',
                    'relationships-vision': 'Strategic vision paired with relationship depth',
                    'strategy-execution': 'Strategic planning meets efficient delivery',
                    'execution-strategy': 'Strategic planning meets efficient delivery',
                    'revenue-relationships': 'Revenue focus paired with relationship building',
                    'relationships-revenue': 'Revenue focus paired with relationship building',
                    'creative-marketing': 'Creative ideas meet market positioning',
                    'marketing-creative': 'Creative ideas meet market positioning',
                    'operations-execution': 'Process excellence meets reliable delivery',
                    'execution-operations': 'Process excellence meets reliable delivery'
                  };
                  const key1 = `${lanes1[0]}-${lanes2[0]}`;
                  const key2 = `${lanes2[0]}-${lanes1[0]}`;
                  const description = uniqueDescriptions[key1] || uniqueDescriptions[key2] || 
                    `${pair.member1.name.split(' ')[0]}'s ${lanes1[0]} complements ${pair.member2.name.split(' ')[0]}'s ${lanes2[0]}`;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => handlePairClick(pair.member1, pair.member2)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <div className="flex -space-x-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                          {pair.member1.avatar || pair.member1.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                          {pair.member2.avatar || pair.member2.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {pair.member1.name} & {pair.member2.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {description}
                        </p>
                      </div>
                      <div 
                        className="text-lg font-bold"
                        style={{ color: pair.chemistry.color }}
                      >
                        {pair.chemistry.score}%
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Friction Hotspots - with varied friction types */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Friction Hotspots
              </h3>
              <div className="space-y-3">
                {frictionHotspots.length > 0 ? frictionHotspots.map((pair, idx) => {
                  const frictionType = pair.chemistry.frictionRisks[0];
                  const frictionIcons = {
                    'Pace Clash': { icon: Zap, color: '#F59E0B' },
                    'Detail vs Big Picture': { icon: Layers, color: '#8B5CF6' },
                    'Process vs Creativity': { icon: Shuffle, color: '#EC4899' },
                    'Relationship vs Results': { icon: Target, color: '#EF4444' },
                    'L4 Territory Overlap': { icon: Users, color: '#3B82F6' },
                    'Culture vs Execution': { icon: Heart, color: '#10B981' }
                  };
                  const frictionConfig = frictionIcons[frictionType?.name] || { icon: AlertTriangle, color: '#F59E0B' };
                  const FrictionIcon = frictionConfig.icon;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => handlePairClick(pair.member1, pair.member2)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors text-left border border-amber-100"
                    >
                      <div className="flex -space-x-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                          {pair.member1.avatar || pair.member1.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                          {pair.member2.avatar || pair.member2.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {pair.member1.name} & {pair.member2.name}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <FrictionIcon className="w-3 h-3" style={{ color: frictionConfig.color }} />
                          <p className="text-xs truncate" style={{ color: frictionConfig.color }}>
                            {frictionType?.name || 'Potential friction'}
                          </p>
                        </div>
                      </div>
                      <div 
                        className="text-lg font-bold"
                        style={{ color: pair.chemistry.color }}
                      >
                        {pair.chemistry.score}%
                      </div>
                    </button>
                  );
                }) : (
                  <p className="text-gray-500 text-center py-4">No significant friction detected</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Matrix View */}
      {viewMode === 'matrix' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 overflow-x-auto">
          <h3 className="font-bold text-gray-900 mb-4">Compatibility Matrix</h3>
          <p className="text-sm text-gray-500 mb-4">Click any cell to see detailed chemistry analysis</p>
          <div className="min-w-[800px]">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="p-2"></th>
                  {teamMembers.slice(0, 8).map(member => (
                    <th key={member.id} className="p-2 text-xs font-medium text-gray-500 text-center">
                      <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 text-xs font-bold">
                        {member.avatar || member.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="mt-1 block truncate max-w-[60px]">{member.name.split(' ')[0]}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamMembers.slice(0, 8).map((member1, i) => (
                  <tr key={member1.id}>
                    <td className="p-2 text-xs font-medium text-gray-600 text-right whitespace-nowrap">
                      {member1.name.split(' ')[0]}
                    </td>
                    {teamMembers.slice(0, 8).map((member2, j) => {
                      if (i === j) {
                        return (
                          <td key={member2.id} className="p-1">
                            <div className="w-12 h-12 mx-auto rounded-lg bg-gray-100" />
                          </td>
                        );
                      }
                      if (i > j) {
                        return (
                          <td key={member2.id} className="p-1">
                            <div className="w-12 h-12 mx-auto" />
                          </td>
                        );
                      }
                      const chemistry = calculateChemistry(member1, member2);
                      return (
                        <td key={member2.id} className="p-1">
                          <button
                            onClick={() => handlePairClick(member1, member2)}
                            className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center text-white text-sm font-bold hover:scale-110 transition-transform cursor-pointer"
                            style={{ backgroundColor: chemistry.color }}
                            title={`${member1.name} & ${member2.name}: ${chemistry.score}%`}
                          >
                            {chemistry.score}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-center gap-6 mt-4 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-500" />
              <span>Excellent (85+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#00C9A7' }} />
              <span>Good (70-84)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-500" />
              <span>Fair (55-69)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500" />
              <span>Challenging (&lt;55)</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Pair Analysis View */}
      {viewMode === 'pair' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pair Selector */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-4">Select a Pair</h3>
            <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
              {teamMembers.map(member => (
                <button
                  key={member.id}
                  onClick={() => {
                    if (!selectedPair?.member1 || selectedPair?.member2) {
                      setSelectedPair({ member1: member, member2: null, chemistry: null });
                    } else if (member.id !== selectedPair.member1.id) {
                      const chemistry = calculateChemistry(selectedPair.member1, member);
                      setSelectedPair({ ...selectedPair, member2: member, chemistry });
                    }
                  }}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    selectedPair?.member1?.id === member.id || selectedPair?.member2?.id === member.id
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-xs font-bold">
                      {member.avatar || member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">{member.title}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {selectedPair?.member1 && !selectedPair?.member2 && (
              <p className="text-sm text-pink-600 mt-4 text-center">
                Now select a second person to analyze chemistry
              </p>
            )}
          </div>
          
          {/* Chemistry Results */}
          <div>
            {selectedPair?.chemistry ? (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="chemistry-result">
                {/* Header */}
                <div 
                  className="p-5"
                  style={{ background: `linear-gradient(135deg, ${selectedPair.chemistry.color}20 0%, ${selectedPair.chemistry.color}05 100%)` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-bold border-3 border-white">
                          {selectedPair.member1.avatar || selectedPair.member1.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold border-3 border-white">
                          {selectedPair.member2.avatar || selectedPair.member2.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{selectedPair.member1.name}</p>
                        <p className="text-sm text-gray-500">& {selectedPair.member2.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p 
                        className="text-4xl font-black"
                        style={{ color: selectedPair.chemistry.color }}
                      >
                        {selectedPair.chemistry.score}%
                      </p>
                      <p 
                        className="text-sm font-medium"
                        style={{ color: selectedPair.chemistry.color }}
                      >
                        {selectedPair.chemistry.label} Chemistry
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Strengths */}
                {selectedPair.chemistry.strengths.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Collaboration Strengths
                    </h4>
                    <ul className="space-y-2">
                      {selectedPair.chemistry.strengths.map((strength, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                          {strength}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Friction Risks */}
                {selectedPair.chemistry.frictionRisks.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Potential Friction Points
                    </h4>
                    {selectedPair.chemistry.frictionRisks.map((risk, idx) => (
                      <div key={idx} className="mb-4 last:mb-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-gray-900">{risk.name}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{risk.description}</p>
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                          <p className="text-xs font-semibold text-amber-800 mb-2">Communication Bridges:</p>
                          <ul className="space-y-1">
                            {risk.bridges.map((bridge, bidx) => (
                              <li key={bidx} className="text-xs text-amber-700 flex items-start gap-2">
                                <Zap className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {bridge}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Additional Factors */}
                {selectedPair.chemistry.factors.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Other Considerations</h4>
                    <ul className="space-y-2">
                      {selectedPair.chemistry.factors.map((factor, idx) => (
                        <li 
                          key={idx} 
                          className={`text-sm flex items-start gap-2 ${
                            factor.type === 'risk' ? 'text-amber-600' : 'text-gray-600'
                          }`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                            factor.type === 'risk' ? 'bg-amber-500' : 'bg-gray-400'
                          }`} />
                          {factor.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 flex flex-col items-center justify-center text-center h-full">
                <HeartHandshake className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500">Select two team members to analyze their chemistry</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================
// MAIN INTELLIGENCE PAGE
// ================================================
const Intelligence = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'coaching');
  
  // Update active tab if URL param changes
  useEffect(() => {
    if (tabParam && ['coaching', 'optimizer', 'simulation', 'chemistry'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);
  
  const tabs = [
    { id: 'coaching', label: 'Cognitive Coach', icon: MessageSquare, color: AI_COLORS.primary },
    { id: 'optimizer', label: 'Team Optimizer', icon: Users, color: AI_COLORS.blue },
    { id: 'simulation', label: 'Org Simulation', icon: BarChart3, color: AI_COLORS.purple },
    { id: 'chemistry', label: 'Team Chemistry', icon: HeartHandshake, color: AI_COLORS.chemistry }
  ];
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: AI_COLORS.background }} data-testid="intelligence-page">
      {/* Premium Dark Header */}
      <div 
        className="px-6 py-5 border-b"
        style={{ 
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
          borderColor: 'rgba(255,255,255,0.1)'
        }}
      >
        <div className="flex items-center gap-4 mb-5">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ 
              background: 'linear-gradient(135deg, #00C9A7 0%, #059669 100%)',
              boxShadow: '0 0 30px rgba(0, 201, 167, 0.4)'
            }}
          >
            <Brain className="w-7 h-7 text-white" style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.5))' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Intelligence Center</h1>
            <p className="text-gray-400 text-sm">
              The only organizational intelligence that understands how your team thinks — not just what they produce.
            </p>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                activeTab === tab.id 
                  ? 'text-white shadow-lg' 
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
              }`}
              style={{ 
                backgroundColor: activeTab === tab.id ? tab.color : 'transparent',
                boxShadow: activeTab === tab.id ? `0 0 20px ${tab.color}40` : 'none'
              }}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="max-w-7xl mx-auto">
        {activeTab === 'coaching' && <CoachingTab />}
        {activeTab === 'optimizer' && <TeamOptimizerTab />}
        {activeTab === 'simulation' && <OrgSimulationTab />}
        {activeTab === 'chemistry' && <TeamChemistryTab />}
      </div>
    </div>
  );
};

export default Intelligence;
